import React, { useState, useEffect } from 'react';
import {
  Mail,
  Users,
  Database,
  RefreshCw,
  AlertCircle,
  UserCircle,
  X,
  CreditCard,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Shield,
  Lock
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const SettingsPage = ({
  gmailStatus,
  checkGmailStatus,
  connectToGmail,
  zohoConnected,
  connectToZoho,
  setGmailStatus
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [, setResponseMonitorStatus] = useState({
    isActive: false,
    hasGmailClient: false
  });

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'user' });
  const [inviteLoading, setInviteLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [teamInfo, setTeamInfo] = useState(null);
  const [teamInfoLoading, setTeamInfoLoading] = useState(false);
  const [teamInfoError, setTeamInfoError] = useState(null);
  const [activeSection, setActiveSection] = useState('profile');
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptionCapabilities, setSubscriptionCapabilities] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [billingAction, setBillingAction] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current: '',
    next: '',
    confirm: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const isAdmin = user?.role === 'admin';
  const capabilityData = subscriptionInfo?.capabilities || subscriptionCapabilities;
  const derivedTeamId = teamInfo?.id ?? user?.teamId ?? user?.team_id ?? null;
  const isZohoEnabledTeam = Number(derivedTeamId) === 1;
  const seatLimitValue =
    capabilityData?.seatLimit ??
    subscriptionInfo?.seat_limit ??
    subscriptionInfo?.plan_details?.default_seat_limit ??
    null;
  const normalizedSeatLimit =
    typeof seatLimitValue === 'number'
      ? seatLimitValue
      : seatLimitValue != null && !Number.isNaN(Number(seatLimitValue))
        ? Number(seatLimitValue)
        : null;
  const seatsUsed =
    typeof capabilityData?.seatsUsed === 'number' ? capabilityData.seatsUsed : null;
  const seatUsageLabel =
    normalizedSeatLimit != null
      ? seatsUsed != null
        ? `${seatsUsed}/${normalizedSeatLimit} seats used`
        : `${normalizedSeatLimit} seats included`
      : null;
  const seatLimitDisplay =
    normalizedSeatLimit != null
      ? normalizedSeatLimit
      : subscriptionInfo?.seat_limit ||
      subscriptionInfo?.plan_details?.default_seat_limit ||
      '—';
  const canSendInvites = capabilityData?.canSendInvites ?? true;
  const canSendEmails = capabilityData?.canSendEmails ?? true;
  const inviteBlockedReason = !canSendInvites
    ? 'Seat limit reached. Upgrade your plan or deactivate existing teammates before inviting more users.'
    : null;
  const emailBlockedReason = !canSendEmails
    ? 'Email sending requires either an active paid subscription or the workspace owner account.'
    : null;

  useEffect(() => {
    checkResponseMonitorStatus();
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user?.role]);

  useEffect(() => {
    if (user) {
      fetchTeamInfo();
    }
  }, [user?.id]);

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await axios.get('/api/auth/users');
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchTeamInfo = async () => {
    try {
      setTeamInfoLoading(true);
      setTeamInfoError(null);
      const response = await axios.get('/api/auth/team-info');
      if (response.data.success) {
        setTeamInfo(response.data.team);
      } else {
        setTeamInfo(null);
      }
    } catch (error) {
      console.error('Failed to fetch team info:', error);
      setTeamInfoError(error.response?.data?.error || 'Failed to load team info');
    } finally {
      setTeamInfoLoading(false);
    }
  };

  const normalizeWebsiteUrl = (url) => {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const checkResponseMonitorStatus = async () => {
    try {
      const response = await axios.get('/api/email-response-monitor/status');
      if (response.data.success) {
        setResponseMonitorStatus(response.data.status);
      }
    } catch (error) {
      console.error('Failed to check response monitor status:', error);
    }
  };

  const handleConnectGmail = async () => {
    setLoading(true);
    await connectToGmail();
    setLoading(false);
  };

  const handleSetPrimaryAccount = async (accountId) => {
    try {
      setLoading(true);
      await axios.put(`/api/gmail/accounts/${accountId}/set-primary`);
      await checkGmailStatus();
      alert('Primary account updated successfully!');
    } catch (error) {
      console.error('Failed to set primary account:', error);
      alert('Failed to set primary account');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to remove this Gmail account?')) return;

    try {
      setLoading(true);
      await axios.delete(`/api/gmail/accounts/${accountId}`);
      await checkGmailStatus();
      alert('Gmail account removed successfully!');
    } catch (error) {
      console.error('Failed to delete account:', error);
      alert('Failed to remove Gmail account');
    } finally {
      setLoading(false);
    }
  };

  const handleReconnectAccount = async (accountId) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gmail/auth-url', {
        params: { reconnect: accountId }
      });

      window.open(response.data.authUrl, '_blank', 'width=600,height=700');

      const checkInterval = setInterval(async () => {
        try {
          const statusResponse = await axios.get('/api/gmail/accounts');
          const latestGmailStatus = {
            hasAccounts: statusResponse.data.accounts.length > 0,
            accounts: statusResponse.data.accounts,
            primaryAccount: statusResponse.data.accounts.find(acc => acc.is_primary)
          };

          setGmailStatus(latestGmailStatus);

          const updatedAccount = latestGmailStatus.accounts.find(acc => acc.id === accountId);
          if (updatedAccount && updatedAccount.is_active !== 0) {
            clearInterval(checkInterval);
            setLoading(false);
            alert('Gmail account reconnected successfully!');
          }
        } catch (error) {
          console.error('Error checking Gmail status during reconnect:', error);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(checkInterval);
        setLoading(false);
      }, 300000);
    } catch (error) {
      console.error('Failed to reconnect Gmail account:', error);
      alert('Failed to reconnect Gmail account');
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteData.email) {
      alert('Please enter an email address');
      return;
    }

    if (!canSendInvites) {
      alert(inviteBlockedReason || 'Your current plan does not allow more seat invites.');
      return;
    }

    try {
      setInviteLoading(true);
      const response = await axios.post('/api/auth/create-user', {
        username: inviteData.email.split('@')[0],
        email: inviteData.email,
        role: inviteData.role
      });

      if (response.data.success) {
        alert('User invited successfully!');
        setShowInviteModal(false);
        setInviteData({ email: '', role: 'user' });
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to invite user:', error);
      alert('Failed to invite user: ' + (error.response?.data?.error || error.message));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (userId, userEmail) => {
    if (!window.confirm(`Resend invitation to ${userEmail}?`)) {
      return;
    }

    try {
      const response = await axios.post(`/api/auth/users/${userId}/resend-invite`);

      if (response.data.success) {
        alert(`Invitation resent successfully to ${userEmail}!`);
        fetchUsers();
      }
    } catch (error) {
      console.error('Failed to resend invite:', error);
      alert('Failed to resend invite: ' + (error.response?.data?.error || error.message));
    }
  };

  const getStatusBadgeClasses = (status) => {
    const normalized = status?.toLowerCase();
    const map = {
      active: 'bg-green-100 text-green-700',
      trialing: 'bg-emerald-100 text-emerald-700',
      past_due: 'bg-yellow-100 text-yellow-700',
      incomplete: 'bg-orange-100 text-orange-700',
      'incomplete_expired': 'bg-orange-100 text-orange-700',
      canceled: 'bg-red-100 text-red-700',
      unpaid: 'bg-red-100 text-red-700'
    };
    return map[normalized] || 'bg-gray-100 text-gray-700';
  };

  const getBillingLabel = (planDetails, intervalKey) => {
    if (!planDetails?.billing || !intervalKey) return 'Not billed';
    const entries = Object.entries(planDetails.billing);
    const found =
      entries.find(
        ([key, option]) =>
          option?.interval === intervalKey || key === intervalKey
      ) || entries[0];
    if (!found) return intervalKey;
    const [, option] = found;
    return option?.label || (option?.interval
      ? option.interval.charAt(0).toUpperCase() + option.interval.slice(1)
      : intervalKey.charAt(0).toUpperCase() + intervalKey.slice(1));
  };

  const formatDateDisplay = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleDateString();
  };

  const fetchSubscriptionInfo = async (options = {}) => {
    const { sessionId } = options;
    try {
      setSubscriptionLoading(true);
      setSubscriptionError(null);
      const config = sessionId ? { params: { session_id: sessionId } } : {};
      const response = await axios.get('/api/subscriptions/current', config);
      if (response.data.success) {
        const subscription = response.data.subscription;
        setSubscriptionCapabilities(subscription?.capabilities || null);
        const isExpired = (() => {
          if (!subscription?.current_period_end) return false;
          const expirationDate = new Date(subscription.current_period_end);
          if (Number.isNaN(expirationDate.getTime())) return false;
          return expirationDate.getTime() < Date.now();
        })();

        setSubscriptionInfo(isExpired ? null : subscription);
      } else {
        setSubscriptionCapabilities(null);
        setSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Failed to load subscription:', error);
      setSubscriptionError(error.response?.data?.error || 'Failed to load subscription');
      setSubscriptionCapabilities(null);
      setSubscriptionInfo(null);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchPlansList = async () => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      const response = await axios.get('/api/subscriptions/plans');
      if (response.data.success) {
        setPlans(response.data.plans || []);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      setPlansError(error.response?.data?.error || 'Failed to load plans');
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    fetchSubscriptionInfo({ sessionId });
    fetchPlansList();

    if (sessionId) {
      params.delete('session_id');
      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    }
  }, []);

  const handleRefreshSubscription = () => {
    fetchSubscriptionInfo();
  };

  const handleStartCheckout = async (planId, interval) => {
    if (!isAdmin) {
      alert('Only administrators can manage billing.');
      return;
    }

    try {
      setBillingAction(`checkout-${planId}-${interval}`);
      const response = await axios.post('/api/subscriptions/checkout-session', {
        planId,
        interval
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('Checkout URL not returned by server');
      }
    } catch (error) {
      console.error('Failed to start checkout:', error);
      alert('Unable to open Stripe checkout: ' + (error.response?.data?.error || error.message));
    } finally {
      setBillingAction(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!isAdmin) {
      alert('Only administrators can open the billing portal.');
      return;
    }

    try {
      setBillingAction('portal');
      const response = await axios.post('/api/subscriptions/customer-portal');
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('Billing portal URL not returned by server');
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Unable to open billing portal: ' + (error.response?.data?.error || error.message));
    } finally {
      setBillingAction(null);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordSuccess('');
    setPasswordError('');

    if (!passwordData.current || !passwordData.next || !passwordData.confirm) {
      setPasswordError('Enter your current password and the new password twice.');
      return;
    }

    if (passwordData.next !== passwordData.confirm) {
      setPasswordError('New password confirmation does not match.');
      return;
    }

    if (passwordData.next.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }

    try {
      setPasswordLoading(true);
      await axios.post('/api/auth/change-password', {
        currentPassword: passwordData.current,
        newPassword: passwordData.next
      });
      setPasswordSuccess('Password updated successfully.');
      setPasswordData({ current: '', next: '', confirm: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to update password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const menuItems = isZohoEnabledTeam ? [
    { id: 'profile', label: 'My Profile' },
    { id: 'subscription', label: 'Subscription & Billing' },
    { id: 'security', label: 'Security' },
    { id: 'integrations', label: 'Integrations · Gmail & Zoho' },
    { id: 'team', label: 'Team' }
  ] : [
    { id: 'profile', label: 'My Profile' },
    { id: 'subscription', label: 'Subscription & Billing' },
    { id: 'security', label: 'Security' },
    { id: 'integrations', label: 'Integrations · Gmail' },
    { id: 'team', label: 'Team' }
  ];

  const userDisplayName = user?.display_name || user?.username || 'User';
  const userInitial = (userDisplayName?.[0] || '?').toUpperCase();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-600">Manage your email accounts and system preferences</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="lg:w-64 flex-shrink-0">
          <div className="bg-white rounded-lg shadow border p-2">
            <div className="flex flex-col gap-2">
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeSection === item.id
                    ? 'bg-blue-600 text-white shadow'
                    : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <section className="flex-1 space-y-6">
          {activeSection === 'profile' && (
            <div className="bg-white rounded-lg shadow border p-6">
              <h3 className="text-lg font-medium text-gray-900">My Profile</h3>
              <p className="text-sm text-gray-600">Personalize how your teammates see you across the portal.</p>

              <div className="flex items-center gap-4 mt-6">
                <div className="h-16 w-16 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-2xl font-semibold">
                  {userInitial}
                </div>
                <div>
                  <div className="text-xl font-semibold text-gray-900">{userDisplayName}</div>
                  <p className="text-gray-600 capitalize">Role: {user?.role || 'Not assigned'}</p>
                  {user?.email && <p className="text-sm text-gray-500 mt-1">{user.email}</p>}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'subscription' && (
            <>
              <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Subscription & Billing</h3>
                    <p className="text-sm text-gray-600">
                      See your current plan, seat limits, and Stripe billing status.
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      onClick={handleRefreshSubscription}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      disabled={subscriptionLoading}
                    >
                      <RefreshCw size={16} className={subscriptionLoading ? 'animate-spin' : ''} />
                      Refresh
                    </button>
                    {isAdmin && subscriptionInfo?.stripe_customer_id && (
                      <button
                        onClick={handleOpenBillingPortal}
                        disabled={billingAction === 'portal'}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60"
                      >
                        {billingAction === 'portal' ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <CreditCard size={16} />
                        )}
                        Manage Billing
                      </button>
                    )}
                  </div>
                </div>

                {subscriptionLoading ? (
                  <div className="py-10 text-center">
                    <div className="inline-flex items-center gap-2 text-gray-500">
                      <Loader2 className="animate-spin" size={20} />
                      <span>Loading subscription...</span>
                    </div>
                  </div>
                ) : subscriptionError ? (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {subscriptionError}
                    {!isAdmin && (
                      <span className="block text-xs text-red-500 mt-1">
                        Contact an administrator if this persists.
                      </span>
                    )}
                  </div>
                ) : subscriptionInfo ? (
                  <div className="mt-6 grid gap-6 lg:grid-cols-2">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm uppercase tracking-wide text-gray-500">Current Plan</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          <span className="text-2xl font-semibold text-gray-900">
                            {subscriptionInfo.plan_name}
                          </span>
                          <span
                            className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeClasses(subscriptionInfo.status)}`}
                          >
                            {subscriptionInfo.status || 'unknown'}
                          </span>
                        </div>
                        {subscriptionInfo.plan_details?.description && (
                          <p className="text-gray-600 mt-2">
                            {subscriptionInfo.plan_details.description}
                          </p>
                        )}
                      </div>

                      {subscriptionInfo.plan_details?.features?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-900">Included features</p>
                          <ul className="space-y-1">
                            {subscriptionInfo.plan_details.features.map((feature) => (
                              <li key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                                <CheckCircle2 className="text-green-500" size={16} />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-gray-500">Seat limit</p>
                        <p className="text-xl font-semibold text-gray-900 mt-1">
                          {seatLimitDisplay}
                        </p>
                        {seatUsageLabel && (
                          <p className="text-sm text-gray-500 mt-1">
                            {seatUsageLabel}
                          </p>
                        )}
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-gray-500">Billing interval</p>
                        <p className="text-xl font-semibold text-gray-900 mt-1">
                          {subscriptionInfo.billing_interval
                            ? getBillingLabel(subscriptionInfo.plan_details, subscriptionInfo.billing_interval)
                            : subscriptionInfo.plan_details?.billable
                              ? 'Pending'
                              : 'Included'}
                        </p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-gray-500">Next renewal</p>
                        <p className="text-xl font-semibold text-gray-900 mt-1">
                          {formatDateDisplay(subscriptionInfo.current_period_end)}
                        </p>
                      </div>
                      <div className="border rounded-lg p-4">
                        <p className="text-sm text-gray-500">Stripe subscription</p>
                        <p className="text-sm font-mono text-gray-900 mt-1 break-all">
                          {subscriptionInfo.stripe_subscription_id || 'Not created yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 p-4 border border-blue-200 bg-blue-50 rounded-lg text-sm text-blue-900">
                    No subscription data yet. Start a checkout session to activate billing for your workspace.
                  </div>
                )}
              </div>
              {(!subscriptionInfo || subscriptionInfo.plan != "enterprise") && (<div className="bg-white rounded-lg shadow border p-6">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">Plan Catalog</h4>
                      <p className="text-sm text-gray-600">
                        Choose the plan that fits your team. Stripe securely handles upgrades and downgrades.
                      </p>
                    </div>
                    {plansError && (
                      <span className="text-sm text-red-600">{plansError}</span>
                    )}
                  </div>
                </div>

                {plansLoading ? (
                  <div className="py-8 text-center text-gray-500">
                    <Loader2 className="mx-auto mb-3 animate-spin" size={24} />
                    Loading plans...
                  </div>
                ) : plans.length === 0 ? (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    No plans are configured yet. Add plan definitions in <code className="font-mono">utils/plan.json</code>.
                  </div>
                ) : (
                  <div className="mt-6 grid gap-6 md:grid-cols-2">
                    {plans.map((plan) => {
                      const isCurrent = subscriptionInfo?.plan === plan.id;
                      const billingOptions = Object.entries(plan.billing || {});
                      return (
                        <div
                          key={plan.id}
                          className={`border rounded-2xl p-5 flex flex-col h-full ${isCurrent ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                            }`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-wide text-gray-500">
                                {plan.billable ? 'Paid plan' : 'Included'}
                              </p>
                              <h5 className="text-xl font-semibold text-gray-900 mt-1">{plan.name}</h5>
                            </div>
                            {isCurrent && (
                              <span className="px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                                Current
                              </span>
                            )}
                          </div>
                          {plan.description && (
                            <p className="text-sm text-gray-600 mt-2">{plan.description}</p>
                          )}

                          {plan.features?.length > 0 && (
                            <ul className="mt-4 space-y-1 text-sm text-gray-700">
                              {plan.features.map((feature) => (
                                <li key={feature} className="flex items-start gap-2">
                                  <CheckCircle2 className="text-green-500 mt-0.5" size={16} />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}

                          <div className="mt-4 text-sm text-gray-500">
                            Seats included: {plan.default_seat_limit || 1}
                          </div>

                          {plan.billable && billingOptions.length > 0 ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                              {billingOptions.map(([key, option]) => {
                                const interval = option?.interval || key;
                                const actionKey = `checkout-${plan.id}-${interval}`;
                                const isDisabled = isCurrent || !isAdmin;
                                return (
                                  <button
                                    key={key}
                                    onClick={() => handleStartCheckout(plan.id, interval)}
                                    disabled={isDisabled || billingAction === actionKey}
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${isCurrent
                                      ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                                      : !isAdmin
                                        ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                                      }`}
                                  >
                                    {billingAction === actionKey ? (
                                      <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                      <ExternalLink size={16} />
                                    )}
                                    {option.display_price || option.label || `${interval} billing`}
                                  </button>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-600">
                              Included in every workspace. No payment required.
                            </div>
                          )}

                          {!isAdmin && plan.billable && (
                            <p className="mt-3 text-xs text-gray-500">
                              Only administrators can change billing plans.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>)}
            </>
          )}

          {activeSection === 'security' && (
            <div className="bg-white rounded-lg shadow border p-6 space-y-6">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <Shield size={24} className="text-blue-600" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Account Security</h3>
                    <p className="text-sm text-gray-600">
                      Update your password regularly to keep your workspace secure.
                    </p>
                  </div>
                </div>
              </div>

              <form className="space-y-5 max-w-xl" onSubmit={handlePasswordChange}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current password
                  </label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={passwordData.current}
                    onChange={(e) =>
                      setPasswordData((prev) => ({ ...prev, current: e.target.value }))
                    }
                    placeholder="Enter your current password"
                    autoComplete="current-password"
                    required
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New password
                    </label>
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={passwordData.next}
                      onChange={(e) =>
                        setPasswordData((prev) => ({ ...prev, next: e.target.value }))
                      }
                      placeholder="At least 6 characters"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={passwordData.confirm}
                      onChange={(e) =>
                        setPasswordData((prev) => ({ ...prev, confirm: e.target.value }))
                      }
                      placeholder="Repeat new password"
                      autoComplete="new-password"
                      required
                    />
                  </div>
                </div>

                {passwordError && (
                  <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2">
                    <AlertCircle size={16} className="mt-0.5" />
                    <span>{passwordError}</span>
                  </div>
                )}
                {passwordSuccess && (
                  <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-sm text-green-700 flex items-start gap-2">
                    <CheckCircle2 size={16} className="mt-0.5" />
                    <span>{passwordSuccess}</span>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-60"
                  >
                    {passwordLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Lock size={16} />
                    )}
                    Update Password
                  </button>
                  <p className="text-xs text-gray-500">
                    Tip: Use a strong passphrase you haven&apos;t reused elsewhere.
                  </p>
                </div>
              </form>
            </div>
          )}

          {activeSection === 'integrations' && (
            <>
              <div className="bg-white rounded-lg shadow border">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Gmail Integration</h3>
                      <p className="text-sm text-gray-600">Connect your Gmail accounts for sending emails</p>
                      {emailBlockedReason && (
                        <p className="text-xs text-red-600 mt-1">
                          {emailBlockedReason}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleConnectGmail}
                      disabled={loading || !canSendEmails}
                      title={emailBlockedReason || undefined}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg ${loading || !canSendEmails
                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      <Mail size={16} />
                      {loading ? 'Connecting...' : 'Connect Gmail Account'}
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {gmailStatus.hasAccounts ? (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Connected Accounts ({gmailStatus.accounts.length})</h4>
                      <div className="space-y-3">
                        {gmailStatus.accounts.map(account => (
                          <div
                            key={account.id}
                            className={`flex items-center justify-between p-4 border rounded-lg ${account.is_active === 0 ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-3 h-3 rounded-full ${account.is_active === 0 ? 'bg-red-500' : account.is_primary ? 'bg-green-500' : 'bg-gray-300'}`}
                              ></div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {account.email}
                                  {account.is_active === 0 && (
                                    <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Inactive</span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {account.account_name} • Added {new Date(account.created_at).toLocaleDateString()}
                                  {account.is_primary && <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Primary</span>}
                                  {account.is_active === 0 && (
                                    <div className="text-red-600 text-xs mt-1">
                                      ⚠️ Account requires reconnection - authentication token expired
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {account.is_active === 0 ? (
                                <button
                                  onClick={() => handleReconnectAccount(account.id)}
                                  disabled={loading}
                                  className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 disabled:opacity-50"
                                >
                                  <RefreshCw size={14} className="inline mr-1" />
                                  Reconnect
                                </button>
                              ) : (
                                !account.is_primary && (
                                  <button
                                    onClick={() => handleSetPrimaryAccount(account.id)}
                                    disabled={loading}
                                    className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 disabled:opacity-50"
                                  >
                                    Set Primary
                                  </button>
                                )
                              )}
                              <button
                                onClick={() => handleDeleteAccount(account.id)}
                                disabled={loading}
                                className="px-3 py-1 text-sm text-red-600 border border-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="text-sm text-gray-500">
                        The primary account will be used by default for sending emails. You can override this in individual campaigns.
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Mail size={48} className="mx-auto text-gray-400 mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Gmail Accounts Connected</h4>
                      <p className="text-gray-600 mb-4">
                        Connect your Gmail account to send emails directly through Gmail API for better deliverability.
                      </p>
                      <button
                        onClick={handleConnectGmail}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 mx-auto"
                      >
                        <Mail size={16} />
                        {loading ? 'Connecting...' : 'Connect Your First Gmail Account'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {isZohoEnabledTeam && (
                <div className="bg-white rounded-lg shadow border">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Zoho CRM Sync</h3>
                        <p className="text-sm text-gray-600">Synchronize your contacts with Zoho CRM</p>
                      </div>
                      {zohoConnected ? (
                        <p></p>
                      ) : (
                        <button
                          onClick={connectToZoho}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Database size={16} />
                          Connect to Zoho
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-cold-leads');
                              alert(`Cold leads synced: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Cold leads sync failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">To Zoho ↑</h4>
                          <p className="text-sm text-gray-600">Push cold leads to Zoho</p>
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-back-cold-leads');
                              alert(`Cold leads pulled: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Cold leads pull failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">From Zoho ↓</h4>
                          <p className="text-sm text-gray-600">Pull cold leads from Zoho</p>
                        </button>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-warm-prospects');
                              alert(`Warm prospects synced: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Warm prospects sync failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">To Zoho ↑</h4>
                          <p className="text-sm text-gray-600">Push warm prospects to Zoho</p>
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-back-warm-prospects');
                              alert(`Warm prospects pulled: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Warm prospects pull failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">From Zoho ↓</h4>
                          <p className="text-sm text-gray-600">Pull warm prospects from Zoho</p>
                        </button>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-active-clients');
                              alert(`Active clients synced: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Active clients sync failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">To Zoho ↑</h4>
                          <p className="text-sm text-gray-600">Push active clients to Zoho</p>
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const result = await axios.post('/api/zoho-sync/sync-email-campaigns');
                              alert(`Active clients pulled: ${result.data.results.length} processed`);
                            } catch (error) {
                              alert('Active clients pull failed: ' + (error.response?.data?.error || error.message));
                            }
                          }}
                          disabled={!zohoConnected || true}
                          className="w-full p-3 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-left"
                        >
                          <h4 className="font-medium text-gray-900 mb-1">From Zoho ↓</h4>
                          <p className="text-sm text-gray-600">Pull Tasks from Zoho to Email Campaigns</p>
                        </button>
                      </div>
                    </div>

                    {!zohoConnected && (
                      <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex">
                          <AlertCircle size={20} className="text-yellow-600 mr-3 mt-0.5" />
                          <div>
                            <h5 className="font-medium text-yellow-800">Zoho Connection Required</h5>
                            <p className="text-sm text-yellow-700 mt-1">
                              You need to connect to Zoho CRM before you can sync your contacts.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {activeSection === 'team' && (
            <>
              <div className="bg-white rounded-lg shadow border mb-6">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Company Profile</h3>
                    </div>
                    {teamInfo?.name && (
                      <span className="text-sm font-medium text-gray-500 truncate max-w-xs">
                        {teamInfo.name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  {teamInfoLoading ? (
                    <div className="flex items-center text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading team information...
                    </div>
                  ) : teamInfoError ? (
                    <div className="text-sm text-red-600">{teamInfoError}</div>
                  ) : teamInfo ? (
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Company Name</dt>
                        <dd className="text-base text-gray-900 mt-1">{teamInfo.name || '—'}</dd>
                      </div>
                      {teamInfo.website && (
                        <div>
                          <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Website</dt>
                          <dd className="mt-1">
                            <a
                              href={normalizeWebsiteUrl(teamInfo.website)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:underline break-words"
                            >
                              {teamInfo.website}
                            </a>
                          </dd>
                        </div>
                      )}
                      {teamInfo.industry && (
                        <div>
                          <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Industry</dt>
                          <dd className="text-base text-gray-900 mt-1">{teamInfo.industry}</dd>
                        </div>
                      )}
                      {teamInfo.size && (
                        <div>
                          <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Company Size</dt>
                          <dd className="text-base text-gray-900 mt-1">{teamInfo.size}</dd>
                        </div>
                      )}
                      {teamInfo.phone && (
                        <div>
                          <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</dt>
                          <dd className="text-base text-gray-900 mt-1">{teamInfo.phone}</dd>
                        </div>
                      )}
                      {!teamInfo.website && !teamInfo.industry && !teamInfo.size && !teamInfo.phone && (
                        <div className="md:col-span-2 text-sm text-gray-500">
                          No additional company details have been provided yet.
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="text-sm text-gray-500">Team information is unavailable.</p>
                  )}
                </div>
              </div>

              {user?.role === 'admin' ? (
                <div className="bg-white rounded-lg shadow border">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">User Management</h3>
                        <p className="text-sm text-gray-600">Invite new users to your team</p>
                        {seatUsageLabel && (
                          <p className="text-xs text-gray-500 mt-1">
                            {seatUsageLabel}
                          </p>
                        )}
                        {inviteBlockedReason && (
                          <p className="text-xs text-red-600 mt-1">
                            {inviteBlockedReason}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={async () => {
                          let latestStatus = gmailStatus;
                          try {
                            const refreshedStatus = await checkGmailStatus();
                            if (refreshedStatus) {
                              latestStatus = refreshedStatus;
                            }
                          } catch (error) {
                            console.error('Failed to refresh Gmail status before inviting:', error);
                          }

                          const hasActiveGmailAccount = latestStatus?.accounts?.some(
                            account => account && account.is_active !== 0
                          );

                          if (!hasActiveGmailAccount) {
                            alert('Please connect an active Gmail account before inviting users.');
                            return;
                          }

                          setShowInviteModal(true);
                        }}
                        disabled={inviteLoading || !canSendInvites}
                        title={inviteBlockedReason || undefined}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${inviteLoading || !canSendInvites
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                      >
                        <UserCircle size={16} />
                        Invite User
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    {usersLoading ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                        <p className="text-gray-600 mt-2">Loading users...</p>
                      </div>
                    ) : users.length > 0 ? (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">Team Members ({users.length})</h4>
                        <div className="space-y-3">
                          {users.map(teamMember => (
                            <div
                              key={teamMember.id}
                              className={`flex items-center justify-between p-4 border rounded-lg ${teamMember.invite_status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-gray-200'}`}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-3 h-3 rounded-full ${teamMember.invite_status === 'pending' ? 'bg-yellow-500' :
                                    teamMember.invite_status === 'accepted' ? 'bg-green-500' : 'bg-gray-300'
                                    }`}
                                ></div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {teamMember.username}
                                    {teamMember.invite_status === 'pending' && (
                                      <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">Pending</span>
                                    )}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {teamMember.email} • {teamMember.role}
                                    {teamMember.created_at && <span className="ml-2">• Invited {new Date(teamMember.created_at).toLocaleDateString()}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="text-sm text-gray-500">
                                  {teamMember.invite_status === 'pending' ? 'Awaiting acceptance' :
                                    teamMember.invite_status === 'accepted' ? 'Active' : 'Unknown status'}
                                </div>
                                {teamMember.invite_status === 'pending' && (
                                  <button
                                    onClick={() => handleResendInvite(teamMember.id, teamMember.email)}
                                    className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                  >
                                    Resend Invite
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-sm text-gray-500">
                          Users with "Pending" status have been invited but haven't accepted yet.
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <Users size={48} className="mx-auto text-gray-400 mb-4" />
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Team Management</h4>
                        <p className="text-gray-600 mb-4">
                          No team members yet. Click "Invite User" to send an invitation to a new team member.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow border p-6">
                  <h3 className="text-lg font-medium text-gray-900">Team Access</h3>
                  <p className="text-sm text-gray-600">
                    Team management tools are available to administrators. Contact your workspace admin if you need access.
                  </p>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {
        showInviteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Invite New User</h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email:
                  </label>
                  <input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role:
                  </label>
                  <select
                    value={inviteData.role}
                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="producer">Producer</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowInviteModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInviteUser}
                    disabled={inviteLoading}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default SettingsPage;
